import { Button, Checkbox, DatePicker, Form, Input, Modal, Select, message } from 'antd'
import moment from 'moment'
import React, { useState } from 'react'
import {
  SPARTACUS_NOTE_DEFAULT_OUTCOME,
  SPARTACUS_NOTE_DEFAULT_TYPE,
  SPARTACUS_NOTE_OUTCOME_OPTIONS,
  SPARTACUS_NOTE_TEXT_MAX_LENGTH,
  SPARTACUS_NOTE_TYPE_OPTIONS,
} from 'src/constants/spartacusNotes'
import { createSpartacusVenueNote } from 'src/utils/spartacusApi'
import styled from 'styled-components/macro'

const { TextArea } = Input
const { Option } = Select

const EMPTY_FORM = {
  noteText: '',
  noteType: SPARTACUS_NOTE_DEFAULT_TYPE,
  outcome: SPARTACUS_NOTE_DEFAULT_OUTCOME,
  followUpAt: null,
  pinned: false,
}

/**
 * Compose a Spartacus customer note for the deal's primary venue.
 * Notes are stored in Spartacus — Catch Board does not keep a copy.
 * When a note includes a follow-up date, optionally offer to set deal nextFollowUpAt.
 */
const CatchBoardDealNotesSection = ({
  primaryVenueId = null,
  primaryVenueName = '',
  disabled = false,
  onNoteCreated,
  onApplyDealFollowUp,
}) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const offerDealFollowUp = noteFollowUpAt => {
    if (!noteFollowUpAt || !onApplyDealFollowUp) {
      return
    }

    const label = noteFollowUpAt.format
      ? noteFollowUpAt.format('D MMM YYYY')
      : String(noteFollowUpAt)

    Modal.confirm({
      title: 'Also set deal follow-up?',
      content: `Update this deal’s next follow-up to ${label} so it shows on the Catch Board card?`,
      okText: 'Set deal follow-up',
      cancelText: 'Not now',
      onOk: () => onApplyDealFollowUp(noteFollowUpAt),
    })
  }

  const handleSubmit = async () => {
    if (!primaryVenueId) {
      message.warning('Link a primary venue before adding a note')
      return
    }

    const noteText = String(form.noteText || '').trim()
    if (!noteText) {
      message.warning('Please enter a note before saving')
      return
    }

    const noteFollowUpAt = form.followUpAt || null

    setSubmitting(true)
    try {
      const created = await createSpartacusVenueNote(primaryVenueId, {
        noteText,
        noteType: form.noteType,
        outcome: form.outcome,
        followUpAt: noteFollowUpAt ? noteFollowUpAt.toISOString() : null,
        pinned: Boolean(form.pinned),
      })
      setForm(EMPTY_FORM)
      message.success('Note added in Spartacus')
      onNoteCreated?.(created)
      offerDealFollowUp(noteFollowUpAt)
    } catch (err) {
      message.error(err.message || 'Failed to add note')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Section data-testid='catch-board-deal-notes'>
      <SectionTitle>Spartacus notes</SectionTitle>
      <SectionHint>
        {primaryVenueId
          ? `Saved on ${primaryVenueName || 'the primary venue'} in Spartacus — one source of truth.`
          : 'Link a primary venue to add notes. Notes live in Spartacus, not on the deal.'}
      </SectionHint>

      <Form.Item label='Note' required style={{ marginBottom: 12 }}>
        <TextArea
          rows={3}
          maxLength={SPARTACUS_NOTE_TEXT_MAX_LENGTH}
          value={form.noteText}
          disabled={disabled || !primaryVenueId}
          placeholder='Add a customer note for the team…'
          onChange={event => handleFieldChange('noteText', event.target.value)}
          data-testid='catch-board-deal-note-text'
        />
        <CharacterCount aria-live='polite'>
          {form.noteText.length}/{SPARTACUS_NOTE_TEXT_MAX_LENGTH}
        </CharacterCount>
      </Form.Item>

      <RowFields>
        <Form.Item label='Type' style={{ marginBottom: 12, flex: 1 }}>
          <Select
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 140 }}
            value={form.noteType}
            disabled={disabled || !primaryVenueId}
            onChange={value => handleFieldChange('noteType', value)}
            data-testid='catch-board-deal-note-type'
          >
            {SPARTACUS_NOTE_TYPE_OPTIONS.map(value => (
              <Option key={value} value={value}>
                {value}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label='Outcome' style={{ marginBottom: 12, flex: 1 }}>
          <Select
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 180 }}
            value={form.outcome}
            disabled={disabled || !primaryVenueId}
            onChange={value => handleFieldChange('outcome', value)}
            data-testid='catch-board-deal-note-outcome'
          >
            {SPARTACUS_NOTE_OUTCOME_OPTIONS.map(value => (
              <Option key={value} value={value}>
                {value}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </RowFields>

      <RowFields>
        <Form.Item
          label='Follow-up date'
          style={{ marginBottom: 12, flex: 1 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            value={form.followUpAt}
            allowClear
            format='D MMM YYYY'
            disabled={disabled || !primaryVenueId}
            onChange={value => handleFieldChange('followUpAt', value || null)}
            disabledDate={current =>
              current && current < moment().startOf('day')
            }
            data-testid='catch-board-deal-note-follow-up'
          />
        </Form.Item>
        <Form.Item label=' ' style={{ marginBottom: 12, flex: 1 }}>
          <Checkbox
            checked={form.pinned}
            disabled={disabled || !primaryVenueId}
            onChange={event =>
              handleFieldChange('pinned', event.target.checked)
            }
            data-testid='catch-board-deal-note-pin'
          >
            Pin this note
          </Checkbox>
        </Form.Item>
      </RowFields>

      <Button
        type='primary'
        loading={submitting}
        disabled={disabled || !primaryVenueId}
        onClick={handleSubmit}
        data-testid='catch-board-deal-note-save'
      >
        Add note
      </Button>
    </Section>
  )
}

const Section = styled.div`
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const RowFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;

  > * {
    min-width: 160px;
  }
`

const CharacterCount = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
  margin-top: 6px;
  text-align: right;
`

export default CatchBoardDealNotesSection
